import {
  BKJCheckboxListItem,
  BKJIcon,
  BKJIconTooltip,
  BKJImage,
} from '@bookjane2/bookjane-design-library';
import { ChangeFunctionType } from '@bookjane2/bookjane-design-library/lib/common.types';
import { BKJRadioInput } from 'components/BKJRadioInput/BKJRadioInput';
import { Flex } from 'components/Flex';
import {
  JaneGridWrapper,
  BKJImageWrapper,
  JaneGridCard,
  JaneGridErrorText,
  JaneGridJaneName,
  JaneGridAgencyName,
  JaneGridSelectButton,
  JaneGridWeeklyWorkedHours,
  JaneConflictTooltipWrapper,
  JaneGridLastBid,
} from 'components/AwardJaneGridSelect/AwardJaneGridSelect.styled';
import {
  IJaneGridCardComponentProps,
  IJaneGridSelectProps,
  formatString,
} from 'components/AwardJaneGridSelect/AwardJaneGridSelect.types';

import { FC, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InfiniteScrollLoading } from 'components/InfiniteScrollLoading';
import { ChangeEventType } from '@bookjane2/bookjane-design-library/lib/common.types';
import { useResponsive } from 'providers/ResponsiveProvider/Responsive';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import isUserTimeFormat24hSelector from 'store/selectors/userSelectors/isUserTimeFormat24hSelector';

const WebProfilePicLoadingFallbackComponent = () => <BKJIcon iconName="AvatarFill" />;
const MobileProfilePicLoadingFallbackComponent = () => <BKJIcon iconName="MobileAvatarFill" />;

const AwardJaneGridCardComponent: FC<IJaneGridCardComponentProps> = ({
  type = 'Single',
  max = 1,
  name,
  label,
  index,
  getData,
  id,
  value,
  onChange: _onChange,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const is24hFormat = useSelector(isUserTimeFormat24hSelector);
  const ProfilePicLoadingFallbackComponent = isMobile
    ? MobileProfilePicLoadingFallbackComponent
    : WebProfilePicLoadingFallbackComponent;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = useMemo(() => getData(index), [getData, index]) as any;

  const hasConflicts = useMemo(() => {
    return data?.conflictedShifts?.length > 0;
  }, [data]);

  const onChange: ChangeFunctionType = useCallback(
    (e: ChangeEventType) => {
      _onChange({
        target: {
          name: e.target.name,
          value: e.target.value,
        },
      });
    },
    [_onChange],
  );

  const disabled = !value?.includes(id) && value?.length === max ? true : false;
  const lastBid = data?.jane?.bids?.nodes?.[0]?.acceptedAt
    ? DateTime.fromISO(data?.jane?.bids?.nodes?.[0]?.acceptedAt)
    : null;

  const lastBidText = lastBid
    ? `${lastBid.toFormat('yy/MM/dd')} ${
        is24hFormat
          ? lastBid.toFormat('HH:mm')
          : lastBid.toFormat('h:mma').toLowerCase().slice(0, -1)
      }`
    : `${t('schedule:NA_LABEL')}`;

  return (
    <JaneGridCard>
      <BKJImageWrapper height="96px" width="100%">
        <BKJImage
          height="100%"
          width="100%"
          alt=""
          src={data?.jane?.avatarUrls?.origin || ''}
          variant="Rectangle"
          LoadingComponent={ProfilePicLoadingFallbackComponent}
          FallbackComponent={ProfilePicLoadingFallbackComponent}
        />
      </BKJImageWrapper>

      <Flex flexDirection="column" padding="8px 8px 0 8px" flex="1" width="100%">
        <JaneGridJaneName>
          {data?.jane?.firstName} {data?.jane?.lastName}
        </JaneGridJaneName>
        <JaneGridAgencyName>
          {t('create_shifts:RANKING')}: {data?.teamMemberRank}
        </JaneGridAgencyName>
        <JaneGridAgencyName>
          {data?.jane?.employmentStatus?.name && formatString(data?.jane?.employmentStatus?.name)}
        </JaneGridAgencyName>
        <JaneGridAgencyName>{data?.jane?.primaryPosition?.details?.name}</JaneGridAgencyName>
        <JaneGridWeeklyWorkedHours>
          {t('shift_modal:HOURS_WORKED', {
            hours: Number(data?.jane?.totalWeeklyHours?.weeklyHours?.toFixed(2)),
          })}
          <JaneGridLastBid>
            {lastBidText}
            <Flex margin="2px 0 0 auto">
              <BKJIconTooltip iconName="Info" placement="top" css="marginleft: auto">
                <Flex width="178px">Date and time of last awarded shift</Flex>
              </BKJIconTooltip>
            </Flex>
          </JaneGridLastBid>
        </JaneGridWeeklyWorkedHours>
      </Flex>

      <JaneGridSelectButton
        alignItems="center"
        height="44px"
        minHeight="44px"
        maxHeight="44px"
        width="100%"
        minWidth="100%"
        padding="0 10px"
      >
        {type === 'Single' && (
          <BKJRadioInput
            {...{
              name,
              value: id,
              checked: value.includes(id),
              onChange,
              label,
              width: '100%',
            }}
          />
        )}
        {type === 'Multiple' && (
          <BKJCheckboxListItem
            {...{
              label,
              selected: value,
              value: id,
              name,
              disabled,
              onChange,
            }}
          />
        )}
        {hasConflicts && (
          <BKJIconTooltip iconName="Warning" placement="top">
            <JaneConflictTooltipWrapper flexDirection="column" gap="20px">
              <div>
                The member has a conflicting shift for this date. Awarding this team member will
                release the conflicting shift from their schedule.
              </div>
            </JaneConflictTooltipWrapper>
          </BKJIconTooltip>
        )}
      </JaneGridSelectButton>
    </JaneGridCard>
  );
};
export const AwardJaneGridSelect: FC<IJaneGridSelectProps> = ({
  loadMore,
  paginationType = 'Normal',
  type = 'Single',
  label = 'create_shifts:SELECT',
  max = 1,
  context,
  name,
  value,
  onChange,
  errorText = 'create_shifts:THERE_ARE_NO_TEAM_MEMBERS_AVAILABLE',
}) => {
  const { t } = useTranslation();
  const { data } = useContext(context);
  const order = data?.order;

  const janes = useMemo(() => order?.bids || [], [order?.bids]);
  const pageInfo = order?.pageInfo || {};

  const getData = useCallback(
    (index) => {
      return { ...janes[index] };
    },
    [janes],
  );

  if (janes?.length === 0)
    return (
      <Flex
        width="100%"
        flex="1"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <JaneGridErrorText>{t(errorText)}</JaneGridErrorText>
      </Flex>
    );

  return (
    <JaneGridWrapper flexWrap="wrap" width="100%">
      {janes?.map((data: { jane: { id: string } }, index: number) => {
        return (
          <AwardJaneGridCardComponent
            max={max}
            type={type}
            getData={getData}
            id={data?.jane?.id}
            index={index}
            label={t(label)}
            key={data?.jane?.id}
            name={name}
            value={value}
            onChange={onChange}
          />
        );
      })}
      {paginationType === 'InfiniteScroll' && loadMore && !!pageInfo.hasNextPage ? (
        <Flex justifyContent="center" alignItems="center" width="100%" padding="24px 0px 0px 0px">
          <InfiniteScrollLoading onViewportEnter={loadMore!} />
        </Flex>
      ) : null}
    </JaneGridWrapper>
  );
};
